import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import { faReply } from "@fortawesome/free-solid-svg-icons/faReply";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DashboardTodayResponseDto } from "../../../../model/dashboard/dto/DashboardTodayResponseDto";
import { Entry } from "../../../../model/entry/Entry";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { standardDate } from "../../../../utils/Date";
import { getEntryLabel } from "../../../../utils/entryUtil";
import { whatsappChatTitle } from "../../../integration/utils/whatsapp-chat-title";
import { useDrawersContext } from "../../../tasks/drawers-context";
import {
  DashboardEmpty,
  DashboardList,
  DashboardRow,
  DashboardRowAction,
  DashboardRowMeta,
  DashboardRowTag,
  DashboardRowTitle,
  DashboardRowWhat,
} from "../DashboardListRow";
import { DashboardPanel } from "../DashboardPanel";
import { DashboardTaskRow } from "../DashboardTaskRow";

type DashboardTodayAttentionProps = {
  today: DashboardTodayResponseDto;
};

export const DashboardTodayAttention: React.FC<
  DashboardTodayAttentionProps
> = ({ today }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setEntry, setEntryAssignVisible } = useDrawersContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const rows = [
    ...today.overdueTasks.map((task) => (
      <DashboardTaskRow key={`task-${task._id}`} task={task} overdue />
    )),
    ...today.waitingChats.map((chat) => (
      <DashboardRow key={`chat-${chat._id}`}>
        <DashboardRowTag $tone="success">
          {t("dashboard.today.tag.whatsapp")}
        </DashboardRowTag>
        <DashboardRowWhat>
          <DashboardRowTitle to={`/integration/whatsapp-meta?chat=${chat._id}`}>
            <span dir="auto">{whatsappChatTitle(chat)}</span>
          </DashboardRowTitle>
          <DashboardRowMeta>
            {t("dashboard.today.meta.waiting_since", {
              time: standardDate(chat.lastMessageAt, user, true),
            })}
          </DashboardRowMeta>
        </DashboardRowWhat>
        <DashboardRowAction
          icon={faReply}
          label={t("dashboard.today.action.reply")}
          onClick={() =>
            navigate(`/integration/whatsapp-meta?chat=${chat._id}`)
          }
        />
      </DashboardRow>
    )),
    ...today.dueTodayTasks.map((task) => (
      <DashboardTaskRow key={`task-${task._id}`} task={task} overdue={false} />
    )),
    ...today.unassignedEntries.map((entry) => (
      <DashboardRow key={`entry-${entry._id}`}>
        <DashboardRowTag $tone="lightAccent">
          {t("dashboard.today.tag.new_lead")}
        </DashboardRowTag>
        <DashboardRowWhat>
          <DashboardRowTitle to={`/entries?previewEntryId=${entry._id}`}>
            <span dir="auto">{getEntryLabel(entry as Partial<Entry>)}</span>
          </DashboardRowTitle>
          <DashboardRowMeta>
            {t("dashboard.today.meta.unassigned", {
              date: standardDate(entry.createdAt, user, true),
            })}
          </DashboardRowMeta>
        </DashboardRowWhat>
        <DashboardRowAction
          icon={faUserPlus}
          label={t("common.assign")}
          onClick={() => {
            setEntry(entry as unknown as Entry);
            setEntryAssignVisible(true);
          }}
        />
      </DashboardRow>
    )),
    ...today.staleDeals.map((deal) => (
      <DashboardRow key={`deal-${deal._id}`}>
        <DashboardRowTag $tone="warning">
          {t("dashboard.today.tag.stalled")}
        </DashboardRowTag>
        <DashboardRowWhat>
          <DashboardRowTitle to="/deals">{deal.title}</DashboardRowTitle>
          <DashboardRowMeta>
            {t("dashboard.today.meta.stalled", {
              column: deal.columnName,
              date: standardDate(deal.updatedAt, user),
            })}
          </DashboardRowMeta>
        </DashboardRowWhat>
        <DashboardRowAction
          icon={faArrowUpRightFromSquare}
          label={t("dashboard.today.action.open")}
          onClick={() => navigate("/deals")}
        />
      </DashboardRow>
    )),
  ];

  const hiddenOverdue = today.tasks.overdue - today.overdueTasks.length;

  return (
    <DashboardPanel
      title={t("dashboard.today.attention.title")}
      dataTestId={TestIDs.dashboard.today.attention}
    >
      {rows.length ? (
        <DashboardList>{rows}</DashboardList>
      ) : (
        <DashboardEmpty>{t("dashboard.today.attention.empty")}</DashboardEmpty>
      )}

      {hiddenOverdue > 0 ? (
        <DashboardRowTitle to="/tasks">
          {t("dashboard.today.attention.more_overdue", {
            count: hiddenOverdue,
          })}
        </DashboardRowTitle>
      ) : null}
    </DashboardPanel>
  );
};
